// api.js - API service with increased timeout for Render cold starts

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Increased timeout for cold starts (Render takes 30-60s to wake up)
const COLD_START_TIMEOUT = 60000; // 60 seconds
const NORMAL_TIMEOUT = 30000; // 30 seconds

/**
 * Fetch with timeout wrapper
 */
const fetchWithTimeout = async (url, options = {}, timeoutMs = NORMAL_TIMEOUT) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeoutMs/1000}s`);
    }
    throw error;
  }
};

const api = {
  /**
   * Check backend health
   * Uses extended timeout for cold starts
   */
  checkHealth: async () => {
    try {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/`, 
        { method: 'GET' },
        COLD_START_TIMEOUT // Give Render time to wake up
      );
      
      if (!response.ok) {
        throw new Error(`Health check failed: ${response.status}`);
      }
      
      const data = await response.json();
      return data.status === 'healthy';
    } catch (error) {
      console.error('Health check error:', error);
      throw error;
    }
  },

  /**
   * Send a message in a chat session
   */
  sendMessage: async (userId, sessionId, message) => {
    try {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/chat`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            session_id: sessionId,
            message: message
          })
        },
        NORMAL_TIMEOUT
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send message');
      }

      const data = await response.json();
      return data.ai_response;
    } catch (error) {
      console.error('Send message error:', error);
      throw error;
    }
  },

  /**
   * Create a new session
   */
  createSession: async (userId, userName) => {
    try {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/sessions/new`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            name: userName
          })
        },
        NORMAL_TIMEOUT
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create session');
      }

      return await response.json();
    } catch (error) {
      console.error('Create session error:', error);
      throw error;
    }
  },

  /**
   * Get all sessions for a user
   */
  getUserSessions: async (userId) => {
    try {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/users/${encodeURIComponent(userId)}/sessions`,
        { method: 'GET' },
        NORMAL_TIMEOUT
      );

      if (!response.ok) {
        if (response.status === 404) {
          return []; // No sessions found
        }
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get sessions');
      }

      return await response.json();
    } catch (error) {
      console.error('Get sessions error:', error);
      throw error;
    }
  },

  /**
   * Get conversation history for a session
   */
  getConversation: async (userId, sessionId) => {
    try {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/conversations/${sessionId}?user_id=${encodeURIComponent(userId)}`,
        { method: 'GET' },
        NORMAL_TIMEOUT
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get conversation');
      }

      return await response.json();
    } catch (error) {
      console.error('Get conversation error:', error);
      throw error;
    }
  }
};

export default api;